<template>
  <div
    class="tooltip-wrapper"
    ref="trigger"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @mousedown="handleMouseDown"
    @mouseup="handleMouseUp"
    @click="handleClick"
  >
    <slot></slot>
    <transition name="tooltip-fade">
      <div
        v-if="visible"
        ref="tooltip"
        class="tooltip"
        :class="[`tooltip-${anchor}`, `tooltip-${offset > 0 ? 'offset' : 'no-offset'}`, { 'tooltip-click-mode': clickMode, 'tooltip-fixed-width': !!width }]"
        :style="tooltipStyle"
      >
        <div v-if="text.length > 0" class="tooltip-content">
          {{ text }}
        </div>
        <div v-else class="tooltip-content">
          <slot name="tooltip-content"></slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// Below this window width the tooltip toggles on click/tap instead of showing on hover.
const MOBILE_MAX_WIDTH = 768;

export default {
  name: "TwTooltipNew",
  props: {
    text: {
      type: String,
      default: ""
    },
    anchor: {
      type: String,
      default: "bottom middle",
      validator: (value) => {
        const valid = [
          'top left', 'top middle', 'top right',
          'bottom left', 'bottom middle', 'bottom right',
          'left top', 'left middle', 'left bottom',
          'right top', 'right middle', 'right bottom'
        ];
        return valid.includes(value);
      }
    },
    offset: {
      type: Number,
      default: 4
    },
    delay: {
      type: Number,
      default: 0
    },
    width: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      visible: false,
      timeoutId: null,
      tooltipStyle: {},
      // True while the tooltip is open from a mobile click/tap (as opposed to hover).
      clickMode: false
    };
  },
  methods: {
    isMobile() {
      return typeof window !== "undefined" && window.innerWidth < MOBILE_MAX_WIDTH;
    },
    handleMouseEnter() {
      // Taps on touch devices emulate mouseenter as well; on mobile only clicks control the tooltip.
      if (this.isMobile()) return;
      this.show();
    },
    handleMouseLeave() {
      // A click-opened tooltip only closes via another click on the trigger or an outside click/touch.
      if (this.clickMode) return;
      this.hide();
    },
    handleMouseDown(event) {
      this.stopMobileTapPropagation(event);
    },
    handleMouseUp(event) {
      this.stopMobileTapPropagation(event);
    },
    handleClick(event) {
      if (!this.clickMode && !this.isMobile()) return;

      // On mobile the click belongs to the tooltip; don't let a wrapping card/row treat it as a click on itself.
      event.stopPropagation();

      // Clicks inside the open tooltip (e.g. on slot content) keep it open.
      if (this.eventHits(this.$refs.tooltip, event)) return;

      if (this.visible) {
        this.hide();
      } else {
        this.clickMode = true;
        this.open();
        this.addOutsideListeners();
      }
    },
    stopMobileTapPropagation(event) {
      // Wrapping cards navigate on mousedown/mouseup; on mobile those belong to the tooltip click too.
      if (this.clickMode || this.isMobile()) {
        event.stopPropagation();
      }
    },
    handleOutsideInteraction(event) {
      if (this.eventHits(this.$el, event)) return;
      this.hide();
    },
    addOutsideListeners() {
      document.addEventListener("click", this.handleOutsideInteraction, true);
      document.addEventListener("touchstart", this.handleOutsideInteraction, { capture: true, passive: true });
    },
    removeOutsideListeners() {
      document.removeEventListener("click", this.handleOutsideInteraction, true);
      document.removeEventListener("touchstart", this.handleOutsideInteraction, { capture: true, passive: true });
    },
    eventHits(element, event) {
      if (!element) return false;
      // composedPath() also resolves targets inside the shadow DOM of the web component builds.
      if (typeof event.composedPath === "function") {
        return event.composedPath().indexOf(element) !== -1;
      }
      return element === event.target || element.contains(event.target);
    },
    show() {
      if (this.delay > 0) {
        this.timeoutId = setTimeout(() => {
          this.open();
        }, this.delay);
      } else {
        this.open();
      }
    },
    open() {
      this.visible = true;
      this.$nextTick(this.positionTooltip);
    },
    hide() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
      this.visible = false;
      this.clickMode = false;
      this.removeOutsideListeners();
    },
    positionTooltip() {
      if (!this.$refs.tooltip || !this.$refs.trigger) return;

      // Apply width first if provided
      if (this.width) {
        this.tooltipStyle = { width: this.width };
        this.$nextTick(() => {
          this.calculatePosition();
        });
      } else {
        this.calculatePosition();
      }
    },
    calculatePosition() {
      if (!this.$refs.tooltip || !this.$refs.trigger) return;

      const trigger = this.$refs.trigger;
      const tooltip = this.$refs.tooltip;
      const triggerRect = trigger.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();

      let top = 0;
      let left = 0;

      const [vertical, horizontal] = this.anchor.split(' ');

      // Vertical positioning
      if (vertical === 'top') {
        top = -(tooltipRect.height + this.offset);
      } else if (vertical === 'bottom') {
        top = triggerRect.height + this.offset;
      } else if (vertical === 'left' || vertical === 'right') {
        // For left/right anchors, check horizontal alignment
        if (horizontal === 'top') {
          top = 0;
        } else if (horizontal === 'middle') {
          top = (triggerRect.height - tooltipRect.height) / 2;
        } else if (horizontal === 'bottom') {
          top = triggerRect.height - tooltipRect.height;
        }
      }

      // Horizontal positioning
      if (horizontal === 'left') {
        left = 0;
      } else if (horizontal === 'middle') {
        if (vertical === 'left') {
          left = -(tooltipRect.width + this.offset);
        } else if (vertical === 'right') {
          left = triggerRect.width + this.offset;
        } else {
          left = (triggerRect.width - tooltipRect.width) / 2;
        }
      } else if (horizontal === 'right') {
        left = triggerRect.width - tooltipRect.width;
      } else if (horizontal === 'top' || horizontal === 'bottom') {
        // For left/right anchors with top/bottom alignment
        if (vertical === 'left') {
          left = -(tooltipRect.width + this.offset);
        } else if (vertical === 'right') {
          left = triggerRect.width + this.offset;
        }
      }

      this.tooltipStyle = {
        top: `${top}px`,
        left: `${left}px`,
        ...(this.width && { width: this.width })
      };
    }
  },
  beforeDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.removeOutsideListeners();
  }
};
</script>

<style scoped>
.tooltip-wrapper {
  display: inline-block;
  position: relative;
}

.tooltip {
  position: absolute;
  z-index: 9000;
  padding: 8px 10px;
  background-color: #49494b;
  color: white;
  font-size: 12px;
  line-height: 1.4;
  border-radius: 6px;
  pointer-events: none;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12);
  white-space: nowrap;
}

/* With an explicit width the text wraps inside it instead of stretching the tooltip. */
.tooltip.tooltip-fixed-width {
  white-space: normal;
}

/* When opened by a mobile tap, taps on the tooltip itself must land on it (and not on whatever is behind it). */
.tooltip.tooltip-click-mode {
  pointer-events: auto;
}

.tooltip-content {
  position: relative;
}

/* Fade transition */
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.2s ease;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
}
</style>
